'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const Sheet = DialogPrimitive.Root;

const SheetTrigger = DialogPrimitive.Trigger;

const SheetClose = DialogPrimitive.Close;

const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
      'data-[state=open]:animate-[dialog-overlay-show_200ms_ease-out]',
      'data-[state=closed]:animate-[dialog-overlay-hide_150ms_ease-in]',
      className
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

const sheetVariants = cva(
  // Шторка = модалка: объём ОСТАВЛЕН (ответ 35, второй вопрос = 2).
  'fixed z-50 flex flex-col bg-[var(--surface-primary)] shadow-deep',
  {
    variants: {
      side: {
        // Верхняя и нижняя шторки — ступень screen (32): это «экран внутри
        // экрана». Боковые панели упираются в край экрана всей высотой и
        // скругления не несут — там его и не было.
        top: 'inset-x-0 top-0 border-b border-[var(--border-default)] rounded-b-screen data-[state=open]:animate-[drawer-slide-down_300ms_cubic-bezier(0.32,0.72,0,1)] data-[state=closed]:animate-[drawer-slide-up_200ms_ease-in]',
        bottom: 'inset-x-0 bottom-0 border-t border-[var(--border-default)] rounded-t-screen data-[state=open]:animate-[drawer-slide-up_300ms_cubic-bezier(0.32,0.72,0,1)] data-[state=closed]:animate-[drawer-slide-down_200ms_ease-in]',
        left: 'inset-y-0 left-0 h-full w-3/4 max-w-sm border-r border-[var(--border-default)] data-[state=open]:animate-[drawer-slide-in-left_300ms_cubic-bezier(0.32,0.72,0,1)] data-[state=closed]:animate-[drawer-slide-out-left_200ms_ease-in]',
        right: 'inset-y-0 right-0 h-full w-3/4 max-w-sm border-l border-[var(--border-default)] data-[state=open]:animate-[drawer-slide-in-right_300ms_cubic-bezier(0.32,0.72,0,1)] data-[state=closed]:animate-[drawer-slide-out-right_200ms_ease-in]',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  showClose?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', className, children, showClose = true, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      {children}
      {showClose && (
        <DialogPrimitive.Close
          className={cn(
            'absolute right-4 top-4 p-2 rounded-pill',
            'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]',
            'hover:bg-[var(--surface-secondary)]',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20'
          )}
        >
          <X className="h-4 w-4" weight="bold" />
          <span className="sr-only">Закрыть</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = DialogPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col space-y-2 p-6 pb-0', className)}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-6', className)}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold text-[var(--text-primary)]',
      className
    )}
    {...props}
  />
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-[var(--text-secondary)]', className)}
    {...props}
  />
));
SheetDescription.displayName = DialogPrimitive.Description.displayName;

const SheetBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex-1 overflow-auto p-6', className)} {...props} />
);
SheetBody.displayName = 'SheetBody';

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetBody,
};

